import React from 'react';

import LinkIcon from '@atlaskit/icon/core/link';

import { ProductKeys } from '../../constants/product-keys';
import { useProductConfigs } from '../../constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';
import {
	MICROSOFT_INTEGRATION_KEY,
	getProductFromMicrosoftResourceType,
} from '../../utils/search-result-utils';

import { FileTypeIconTile, getFileTypeIconFor3P } from './file-type-icon-tile';

export const IconFor3P = ({
	product,
	resourceType,
	size,
	subtype,
	thirdPartyConfigs,
}: {
	product?: string;
	resourceType: string;
	size?: 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';
	subtype?: string | null;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): React.JSX.Element => {
	const products = useProductConfigs();

	if (!product) {
		return <LinkIcon color="currentColor" spacing="spacious" label="" />;
	}
	let productKey = product === 'google' ? ProductKeys.GoogleDrive : product;
	/**
	 * Temporary fix for Microsoft integrationKey
	 */
	if (product === MICROSOFT_INTEGRATION_KEY) {
		productKey = getProductFromMicrosoftResourceType(product, resourceType) ?? productKey;
	}

	const FileTypeGlyph = getFileTypeIconFor3P(productKey, subtype);
	if (FileTypeGlyph) {
		const tileLabel = subtype ? `${productKey} ${subtype} icon` : `${productKey} icon`;
		return <FileTypeIconTile Glyph={FileTypeGlyph} label={tileLabel} size={size} />;
	}

	let IconComponent;

	// For Forge connectors, prioritize dynamic API icons over static hardcoded ones
	if (isForgeConnectorProductKey(productKey)) {
		const dynamicConfig = thirdPartyConfigs[productKey];
		if (dynamicConfig) {
			IconComponent = dynamicConfig.searchResultIcon || dynamicConfig.icon;
		}
	}

	// Fallback to static products (for non-Forge or if dynamic config not found)
	if (!IconComponent) {
		IconComponent =
			products[productKey as ProductKeys]?.SearchResultIcon ||
			products[productKey as ProductKeys]?.Icon;
	}

	return IconComponent ? (
		<IconComponent label="" size={size} />
	) : (
		<LinkIcon color="currentColor" spacing="spacious" label="" />
	);
};
